/**
 * PDP adoption gate: for adopted-title products, confirm display via getPdpAdoption.
 * When the API says shouldDisplay is false and the product is tagged adoptedtitle,
 * replaces the PDP content with an inline unavailable message.
 *
 * The loading overlay stays until variant/access UI is ready (unified merge + access
 * filtering) so users do not see layout shifts when the spinner is removed.
 *
 * GET /apps/shopifyData/getPdpAdoption?handle={handle}
 * Response: { success, data: { shouldDisplay, reason, product: { tags, handle, ... } } }
 */

const LOG_PREFIX = '[PDP Adoption Gate]';
const ACCESS_READY_TIMEOUT_MS = 20000;

function tagsIncludeAdoptedTitle(tags) {
  if (!Array.isArray(tags)) return false;
  return tags.some((t) => String(t).toLowerCase() === 'adoptedtitle');
}

/**
 * @returns {Promise<void>}
 */
async function waitForPdpVariantUiReady() {
  if (window.PdpFollettAccess?.waitForPdpFollettAccessReady) {
    await window.PdpFollettAccess.waitForPdpFollettAccessReady();
    return;
  }

  const needsWait =
    !!document.querySelector('.textbook-variant-picker, .format-tabs-wrapper') ||
    !!document.querySelector('[data-fa-pdp-pending]');
  if (!needsWait) return;

  if (!window.__pdpFollettAccessReadyPromise) {
    await new Promise((resolve) => setTimeout(resolve, ACCESS_READY_TIMEOUT_MS));
    return;
  }

  await Promise.race([
    window.__pdpFollettAccessReadyPromise,
    new Promise((resolve) => setTimeout(resolve, ACCESS_READY_TIMEOUT_MS)),
  ]);
}

/**
 * @param {HTMLElement} root - .product-information[data-pdp-adoption-gate]
 */
async function runPdpAdoptionGate(root) {
  const overlay = root.querySelector('.pdp-adoption-gate__overlay');
  const handle = root.dataset.productHandle;
  const variantUiPromise = waitForPdpVariantUiReady();

  if (!handle) {
    console.warn(LOG_PREFIX, 'Missing data-product-handle');
    await variantUiPromise;
    showProductContent(root, overlay);
    return;
  }

  try {
    const body = await fetch(
      `/apps/shopifyData/getPdpAdoption?handle=${encodeURIComponent(handle)}`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      },
    ).then((res) => res.json());

    const data = body && body.success && body.data ? body.data : null;

    if (
      data &&
      data.shouldDisplay === false &&
      tagsIncludeAdoptedTitle(data.product && data.product.tags)
    ) {
      showUnavailable(root, overlay);
      return;
    }
  } catch (e) {
    console.warn(LOG_PREFIX, e);
  }

  await variantUiPromise;
  showProductContent(root, overlay);
}

function showUnavailable(root, overlay) {
  const content = root.querySelector('[data-pdp-adoption-gate-content]');
  const unavailable = root.querySelector('.pdp-adoption-gate__unavailable');

  if (content) content.hidden = true;
  if (unavailable) unavailable.hidden = false;

  root.classList.add('product-information--adoption-unavailable');
  root.removeAttribute('data-pdp-adoption-gate');
  if (overlay) overlay.remove();
}

function showProductContent(root, overlay) {
  const unavailable = root.querySelector('.pdp-adoption-gate__unavailable');
  const content = root.querySelector('[data-pdp-adoption-gate-content]');

  if (content) content.hidden = false;
  if (unavailable) unavailable.remove();

  root.removeAttribute('data-pdp-adoption-gate');
  if (overlay) overlay.remove();
}

function init() {
  document.querySelectorAll('.product-information[data-pdp-adoption-gate]').forEach((root) => {
    runPdpAdoptionGate(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
